import { bootstrapSession, loginUrl, logout, setTransport } from "./api.ts";
import { nativeBridge } from "./bridge.ts";

/**
 * Where sign-in lives. In a browser: the API's cookie session. Inside the Android shell: native
 * OIDC with tokens kept natively; the page only learns "signed in or not".
 */
export const isNative = () => nativeBridge() !== null;

/** The app's two viewports: the interface, which does not scale, and the picture, which does. */
const FIXED = "width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no";
const ZOOMABLE = "width=device-width, initial-scale=1, maximum-scale=6";

const setViewport = (content: string) =>
  document.querySelector('meta[name="viewport"]')?.setAttribute("content", content);

/**
 * Let the screen be pinched while the picture is open, and take it back when it closes.
 *
 * The full-size viewer is the one thing in the app worth getting closer to — a face across a
 * square, the name of a street — and it covers the screen on its own, so what a pinch magnifies
 * there is the photograph and nothing else. Clamping the viewport again on the way out is also
 * how the zoom is undone: a scale outside the limits is pulled back inside them, so the
 * interface is never handed back five times too big with no way to shrink it.
 *
 * In a browser this does nothing, because nothing was taken away there.
 */
export function allowZoom(on: boolean): void {
  if (!isNative()) return;
  setViewport(on ? ZOOMABLE : FIXED);
}

/** Call once at start-up: routes API calls through the shell and hides the keyboard after forms. */
export function installNative(): void {
  const b = nativeBridge();
  if (!b) return;
  setTransport(b.transport);
  // In the app, the interface does not scale. Pinched to five times its size it is unusable and
  // there is no way back to it: the tab bar is off the screen, a card is half past the edge, and
  // the interface is not a page to be read closer. The WebView is told this natively too, but the
  // native switch has not held, and a viewport that states its own limits is one Blink cannot be
  // talked out of. Only here: the same bundle in a browser keeps its zoom, which is a reader's
  // right there. The picture itself is the exception, and asks for it by hand — see [allowZoom].
  setViewport(FIXED);
  // Standing rule: the phone keyboard never stays up over the buttons after typing.
  document.addEventListener(
    "submit",
    () => void b.call("keyboard.hide", {}).catch(() => undefined),
    true,
  );
}

export async function startSession(): Promise<boolean> {
  const b = nativeBridge();
  if (b) return (await b.call("auth.state", {})).signedIn;
  return (await bootstrapSession()) !== null;
}

export function signIn(): void {
  const b = nativeBridge();
  if (b) void b.call("auth.signIn", {}).catch(() => undefined);
  else window.location.assign(loginUrl(window.location.pathname + window.location.hash));
}

export async function signOut(): Promise<void> {
  const b = nativeBridge();
  if (b) await b.call("auth.signOut", {});
  else await logout();
}

/** Native tells the page when sign-in state changes (after the browser hands the code back). */
export function onAuthChanged(fn: (signedIn: boolean) => void): () => void {
  const b = nativeBridge();
  if (!b) return () => undefined;
  const off = b.on("auth.changed", (d) => fn(d.signedIn));
  return () => void off();
}

/** Share an invitation link: the Android share sheet in the shell, the Web Share API or clipboard otherwise. */
export async function shareLink(url: string, title: string): Promise<"shared" | "copied"> {
  const b = nativeBridge();
  if (b) {
    await b.call("share.invite", { url, title });
    return "shared";
  }
  if (typeof navigator.share === "function") {
    await navigator.share({ title, url }).catch(() => undefined);
    return "shared";
  }
  await navigator.clipboard.writeText(url).catch(() => undefined);
  return "copied";
}

/** Starts or stops recording the route on the phone; a no-op in a browser. */
export async function setPhoneLocation(tripId: string, on: boolean): Promise<void> {
  const b = nativeBridge();
  if (!b) return;
  if (on) await b.call("location.start", { tripId });
  else await b.call("location.stop", {});
}
